import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Image, Home, Mail, MapPin, BookOpen, Bot } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { chatbotAPI, type ChatbotConversation } from "@/lib/chatbot-api";

// Import individual admin components
import AdminHeroSlides from "./AdminHeroSlides";
import AdminWelcomeSection from "./AdminWelcomeSection";
import AdminGallery from "./AdminGallery";
import AdminLocation from "./AdminLocation";
import AdminBookSection from "./AdminBookSection";

type MenuItem = 'hero' | 'welcome' | 'gallery' | 'location' | 'contact' | 'chatbot';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuItem>('hero');
  const [chatbotConversations, setChatbotConversations] = useState<ChatbotConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ChatbotConversation | null>(null);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  // Load chatbot conversations when menu changes to chatbot
  useEffect(() => {
    if (activeMenu === 'chatbot') {
      loadChatbotConversations();
    }
  }, [activeMenu]);

  const loadChatbotConversations = async () => {
    try {
      const conversations = await chatbotAPI.getConversations();
      setChatbotConversations(conversations.sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ));
    } catch (error) {
      console.error('Error loading chatbot conversations:', error);
      toast({ title: "Error", description: "Failed to load chatbot conversations", variant: "destructive" });
    }
  };

  const handleDeleteConversation = async (sessionId: string) => {
    if (confirm('Delete this conversation?')) {
      try {
        await chatbotAPI.deleteConversation(sessionId);
        loadChatbotConversations();
        setSelectedConversation(null);
        toast({ title: "Success", description: "Conversation deleted" });
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete conversation", variant: "destructive" });
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
    toast({ title: "Logged out", description: "You have been logged out successfully" });
  };

  if (!isAuthenticated) return null;

  const menuItems = [
    { id: 'hero' as MenuItem, label: 'Hero Slides', icon: Image },
    { id: 'welcome' as MenuItem, label: 'Welcome', icon: Home },
    { id: 'gallery' as MenuItem, label: 'Gallery', icon: Image },
    { id: 'location' as MenuItem, label: 'Location', icon: MapPin },
    { id: 'contact' as MenuItem, label: 'Contact', icon: Mail },
    { id: 'chatbot' as MenuItem, label: 'AI Chatbot', icon: Bot },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <div className="w-64 border-r bg-card flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold">Admin Panel</h1>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveMenu(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeMenu === item.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t space-y-2">
          <Button
            onClick={() => window.open("/", "_blank")}
            variant="outline"
            className="w-full"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            View Site
          </Button>
          <Button
            onClick={handleLogout}
            variant="destructive"
            className="w-full"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {activeMenu === 'hero' && <AdminHeroSlides />}
        {activeMenu === 'welcome' && <AdminWelcomeSection />}
        {activeMenu === 'gallery' && <AdminGallery />}
        {activeMenu === 'location' && <AdminLocation />}
        {activeMenu === 'contact' && <AdminBookSection />}
        {activeMenu === 'chatbot' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">AI Chatbot Conversations</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Conversations</CardTitle>
                    <CardDescription>{chatbotConversations.length} total</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                    {chatbotConversations.map((conv) => (
                      <div
                        key={conv.session_id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                          selectedConversation?.session_id === conv.session_id ? 'bg-muted border-primary' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium truncate">{conv.email || conv.customer_name || 'Anonymous'}</div>
                          {conv.email && <Badge variant="default">Lead</Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(conv.updated_at).toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {conv.messages?.length || 0} messages
                        </div>
                      </div>
                    ))}
                    {chatbotConversations.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No conversations yet
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Conversation Details */}
              <div className="lg:col-span-2">
                {selectedConversation ? (
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>Conversation Details</CardTitle>
                          <CardDescription>
                            {selectedConversation.email && (
                              <div className="mt-2">
                                <strong>Email:</strong> {selectedConversation.email}<br />
                                {selectedConversation.customer_name && <><strong>Name:</strong> {selectedConversation.customer_name}<br /></>}
                                {selectedConversation.phone && <><strong>Phone:</strong> {selectedConversation.phone}</>}
                              </div>
                            )}
                          </CardDescription>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteConversation(selectedConversation.session_id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4 max-h-[500px] overflow-y-auto">
                        {selectedConversation.messages?.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg ${
                              msg.role === 'user'
                                ? 'bg-primary/10 ml-8'
                                : 'bg-muted mr-8'
                            }`}
                          >
                            <div className="font-semibold text-sm mb-1">
                              {msg.role === 'user' ? 'User' : 'AI Assistant'}
                            </div>
                            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                            <div className="text-xs text-muted-foreground mt-2">
                              {new Date(msg.timestamp).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      Select a conversation to view details
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
